import type { GatsbyBrowser } from "gatsby";
import React from "react";
import AppProviders from "./src/providers/AppProviders";
import {
  captureAttributionFromLocation,
  initGoogleAnalytics,
  initGoogleConsentDefaults,
  initMetaPixel,
  pushDataLayerEvent,
  trackGooglePageView,
  trackMetaPageView,
} from "./src/utils/analytics";
import "./src/index.css";
import "./src/i18n";

export const wrapRootElement: GatsbyBrowser["wrapRootElement"] = ({ element }) => (
  <AppProviders>{element}</AppProviders>
);

export const onClientEntry: GatsbyBrowser["onClientEntry"] = () => {
  initGoogleConsentDefaults();
  initMetaPixel();
  initGoogleAnalytics();
  captureAttributionFromLocation();
};

const scrollToTopNow = () => {
  const html = document.documentElement;
  const prevHtml = html.style.scrollBehavior;
  const prevBody = document.body.style.scrollBehavior;
  html.style.scrollBehavior = "auto";
  document.body.style.scrollBehavior = "auto";

  window.scrollTo(0, 0);
  html.scrollTop = 0;
  document.body.scrollTop = 0;
  if (document.scrollingElement) {
    document.scrollingElement.scrollTop = 0;
  }

  html.style.scrollBehavior = prevHtml;
  document.body.style.scrollBehavior = prevBody;
};

/** Listing pages are taller than venue pages — without this, scroll clamps to the bottom. */
export const shouldUpdateScroll: GatsbyBrowser["shouldUpdateScroll"] = ({
  routerProps,
}) => {
  if (routerProps?.location?.hash) {
    return false;
  }
  scrollToTopNow();
  requestAnimationFrame(scrollToTopNow);
  window.setTimeout(scrollToTopNow, 0);
  window.setTimeout(scrollToTopNow, 50);
  return false;
};

export const onRouteUpdate: GatsbyBrowser["onRouteUpdate"] = ({
  location,
  prevLocation,
}) => {
  captureAttributionFromLocation(location.search, location.pathname);

  // Initial page_view is sent by the GA4 config call; repeat it on client navigations.
  if (prevLocation) {
    pushDataLayerEvent("page_view", {
      page_path: location.pathname + location.search + location.hash,
      page_location: window.location.href,
      page_title: document.title,
    });
    trackMetaPageView();
    trackGooglePageView();
  }

  if (location.hash) return;
  scrollToTopNow();
  requestAnimationFrame(scrollToTopNow);
  window.setTimeout(scrollToTopNow, 0);
  window.setTimeout(scrollToTopNow, 50);
  window.setTimeout(scrollToTopNow, 150);
};

export const onPreRouteUpdate: GatsbyBrowser["onPreRouteUpdate"] = ({
  location,
}) => {
  if (!location.hash) {
    scrollToTopNow();
  }
};
